/**
 * Estado da sessão.
 *
 * Fonte única: o `onAuthStateChange` do Supabase. Nada aqui guarda cópia do
 * token — a biblioteca já cuida da renovação, e uma segunda cópia ficaria
 * velha exatamente no momento em que a primeira é renovada.
 */

import type { Session } from '@supabase/auth-js';
import { useEffect } from 'react';
import { create } from 'zustand';

import { auth } from '@/lib/supabase';

interface SessionState {
  session: Session | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  setSession: (session: Session | null) => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  session: null,
  status: 'loading',
  setSession: (session) => set({ session, status: session ? 'authenticated' : 'anonymous' }),
}));

/** Montado uma vez, na raiz da aplicação. */
export function useAuthListener(): void {
  const setSession = useSessionStore((state) => state.setSession);

  useEffect(() => {
    let active = true;

    auth.getSession().then(({ data }) => {
      if (active) setSession(data.session);
    });

    const { data: subscription } = auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [setSession]);
}

/**
 * Um seletor por campo — não um que devolve `{ session, status }`.
 *
 * O Zustand v5 é construído sobre `useSyncExternalStore`, que compara o
 * resultado do seletor por identidade. Um seletor que monta objeto novo a cada
 * chamada nunca é igual ao anterior: o React conclui que a store mudou,
 * renderiza, chama o seletor de novo, e o laço não fecha. A versão anterior
 * disto derrubava o app com "Maximum update depth exceeded" — e só na rota
 * protegida, que é a que usa o hook.
 */
export function useSession() {
  const session = useSessionStore((state) => state.session);
  const status = useSessionStore((state) => state.status);
  return { session, status };
}

export async function signIn(email: string, password: string): Promise<void> {
  const { error } = await auth.signInWithPassword({ email, password });
  if (error) throw error;
}

export async function signUp(email: string, password: string, displayName: string): Promise<void> {
  const { error } = await auth.signUp({
    email,
    password,
    options: {
      // Guardado no `user_metadata` do GoTrue e lido pela API na criação do
      // perfil. Não é fonte de verdade: `profiles.display_name` é, e pode ser
      // alterado depois sem tocar no Supabase.
      data: { display_name: displayName, locale: document.documentElement.lang },
    },
  });
  if (error) throw error;
}

/**
 * Pede o e-mail de redefinição de senha.
 *
 * O `redirectTo` precisa constar em `ADDITIONAL_REDIRECT_URLS` no GoTrue —
 * ele recusa redirecionar para endereço não autorizado, e é essa lista que
 * impede um link de recuperação de levar a pessoa para fora do produto.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const { error } = await auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
}

/** Troca o token do e-mail por uma sessão. Vale para confirmação e recuperação. */
export async function verifyEmailToken(
  tokenHash: string,
  type: 'signup' | 'recovery' | 'email_change',
): Promise<void> {
  const { error } = await auth.verifyOtp({ token_hash: tokenHash, type });
  if (error) throw error;
}

/**
 * Define a senha nova.
 *
 * Só funciona com sessão ativa — que é justamente o que o `verifyEmailToken`
 * acabou de criar a partir do link do e-mail.
 */
export async function updatePassword(password: string): Promise<void> {
  const { error } = await auth.updateUser({ password });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  await auth.signOut();
  // Limpeza explícita do que ficou em disco. O celular pode ser emprestado, e
  // "sair" precisa significar que o próximo a abrir não encontra nada.
  localStorage.removeItem('movhuman.auth');
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(
      keys.filter((k) => k.startsWith('movhuman-data')).map((k) => caches.delete(k)),
    );
  }
}
